import { Box, HeadingProvider, SkeletonProvider, Stack, StyleProp } from '@front10/base-elements';
import * as React from 'react';
import { useQuery } from 'react-query';
import { isSSR } from '@front10/utils';
import { Carousel } from '../../../packages/base-elements/Carousel/Carousel';
import Button from '../../../packages/base-elements/Button2';
import EventCard from '../../items/EventCard';
import { ContactButton } from './ContactButton';
import { Disclaimer } from './Disclaimer';
import {
  EventsDataFetchFn,
  fetchEvents,
  useEventsDataMapper,
  usePlaceholderEvents
} from './eventsData';
import { Filters, useFiltersState } from './Filters';
import { Heading } from './Heading';
import { MoreInfoButton } from './MoreInfoButton';
import { NoResultsAlert } from './NoResultsAlert';
import TicketEventsProvider, { TicketEventsProviderProps } from './TicketEventsProvider';
import { ComponentsConfiguration } from './types';
import { validatePrepopulationData } from './util.js';

interface Props extends TicketEventsProviderProps {
  componentsConfiguration?: ComponentsConfiguration & { carousel?: $TSFixMe };
  fetchData?: EventsDataFetchFn;
  styles?: StyleProp;
  integrationSettings: IntegrationSettings;
  showHeading?: boolean;
  showDisclaimer?: boolean;
  prePopulation?: {};
  queryId?: string;
  headerSettings?: {
    showTitle?: boolean;
    showSubtitle?: boolean;
  };
}

export default function PremiumExperienceGridMobile({
  componentsConfiguration = {},
  fetchData = fetchEvents,
  styles,
  integrationSettings = {},
  showHeading,
  showDisclaimer = true,
  headerSettings = {
    showTitle: true,
    showSubtitle: false
  },
  locale,
  headingLevelStart = 1,
  queryId = 'ticket-events',
  prePopulation
}: Props) {
  return (
    <TicketEventsProvider
      headingLevelStart={headingLevelStart}
      locale={locale}
      filters={componentsConfiguration.filterContainer}
      prePopulation={validatePrepopulationData({
        prePopulation: prePopulation ?? {},
        componentsConfiguration
      })}
    >
      <Stack variant="layout.componentRoot" sx={styles} gap="md">
        {showHeading ? <Heading componentName="events-carousel" {...headerSettings} /> : null}
        <Stack data-container-body gap="md">
          <HeadingProvider>
            <Filters componentName="events-carousel" />
            <Stack gap={3}>
              <DataPanel
                componentName="events-carousel"
                queryId={queryId}
                fetchData={fetchData}
                integrationSettings={integrationSettings['ticketEvents']}
                componentsConfiguration={componentsConfiguration}
              />
              <Box pt="3">{showDisclaimer && <Disclaimer componentName="events-carousel" />}</Box>
            </Stack>
          </HeadingProvider>
        </Stack>
      </Stack>
    </TicketEventsProvider>
  );
}

type DataPanelProps = Required<Pick<Props, 'fetchData' | 'queryId'>> &
  Required<Pick<Props, 'componentsConfiguration' | 'integrationSettings'>> & {
    componentName?: string;
  };

function DataPanel({
  fetchData,
  integrationSettings,
  componentsConfiguration,
  queryId,
  componentName = 'events-carousel'
}: DataPanelProps) {
  const state = useFiltersState((state) => state.state);
  const context = useFiltersState((state) => state.context);
  const onSearch = useFiltersState((state) => state.onSearch);
  const filters = {
    offerType: context,
    ...state[context]
  };
  const [isDialogOpen, setIisDialogOpen] = React.useState(false);
  const placeholderData = usePlaceholderEvents();
  const eventsDataMapper = useEventsDataMapper();
  const {
    status,
    data,
    // error,
    isPlaceholderData
  } = useQuery(
    [queryId, filters, integrationSettings],
    ({ pageParam = 0 }) => {
      return fetchData({ ...integrationSettings, page: pageParam, filters });
    },
    {
      placeholderData: placeholderData,
      select: eventsDataMapper
    }
  );

  const carouselSettings = !isDialogOpen
    ? { ...componentsConfiguration.carousel }
    : {
        ...componentsConfiguration.carousel,
        draggable: false,
        swipe: false
      };

  let content = null;
  if (status === 'success' && data?.length === 0) {
    content = <NoResultsAlert />;
  } else {
    const showPill = componentsConfiguration.card?.showPill ?? true;
    const moreInfoLinkInteractionType =
      componentsConfiguration.card?.moreInfoLinkInteractionType ?? 'popup';
    const contactEmail = componentsConfiguration?.contactEmail;
    const moreInfoTarget = componentsConfiguration?.card?.moreInfoLinkTarget ?? '_blank';
    const ctaTarget = componentsConfiguration?.card?.moreInfoLinkTarget ?? '_blank';
    const cardSettings = componentsConfiguration?.card
      ? {
          ...componentsConfiguration?.card
        }
      : {};
    content = data ? (
      <Box mx={['-5px', null, '38px']} mr={[0, null, '38px']}>
        <Carousel
          key={context}
          elementsToShow={1.15}
          showDots={true}
          showNavigation={false}
          controlsOffset={[0, 0, -50]}
          gap="10px"
          {...carouselSettings}
        >
          {data.map((cardData, index) => {
            const ctaSlot = cardData.ctaUrl ? (
              <Button
                variant="primary"
                // href={cardData.ctaUrl}
                onClick={() => {
                  if (!isSSR()) {
                    window.open(cardData.ctaUrl, ctaTarget);
                  }
                  onSearch(cardData, filters);
                }}
              >
                {cardData.ctaLabel}
              </Button>
            ) : (
              <ContactButton
                data={cardData}
                onClick={() => onSearch(cardData, filters)}
                componentName="events-carousel"
                contactEmail={contactEmail}
                notifyChange={setIisDialogOpen}
              />
            );
            return (
              <EventCard
                key={index}
                {...cardData}
                counter={showPill ? `${index + 1}/${data.length}` : undefined}
                moreInfoSlot={
                  cardData.offerLabel?.toLowerCase() !== 'ticket only' ? (
                    <MoreInfoButton
                      data={{
                        ...cardData,
                        ctaSlot: React.cloneElement(ctaSlot, { autoFocus: true }),
                        moreInfoLinkInteractionType
                      }}
                      notifyChange={setIisDialogOpen}
                      target={moreInfoTarget}
                    />
                  ) : undefined
                }
                ctaSlot={ctaSlot}
                cardSettings={cardSettings}
                componentName={componentName}
              />
            );
          })}
        </Carousel>
      </Box>
    ) : null;
  }

  return <SkeletonProvider isLoading={isPlaceholderData}>{content}</SkeletonProvider>;
}
